import axios from "./axios";

/**
 * One file a user can download to install Akili Transfer (AKL-803).
 *
 * Mirrors the API's `TransferInstallerResponse`. The API reads these from the desktop
 * app's auto-update manifest on the public downloads account, so nothing here is
 * hard-coded on either side and a new release shows up without an edit.
 */
export type TransferInstallerDto = {
  platform: "windows" | "macos";
  /** CPU architecture as the build names it, e.g. "x86_64" or "aarch64". */
  architecture: string;
  format: "exe" | "msi" | "dmg";
  /**
   * The installer to put on the button for its platform. On Windows that is the NSIS
   * `-setup.exe`, because it is what the app's auto-updater installs; the MSI is the
   * alternative for managed installs. Within a platform the API sends this one first.
   */
  recommended: boolean;
  /** Absolute download URL on the public downloads account. */
  url: string;
  /**
   * What the browser will save — already URL-decoded, so it carries the real space in
   * "Akili Transfer_0.1.1_x64-setup.exe". Show it as-is so the user recognises the file.
   */
  file_name: string;
  /** Null when storage did not report a length; the download still works. */
  size_bytes: number | null;
};

/**
 * The Akili Transfer release currently published for download.
 *
 * A platform that does not appear in `installers` has **no build published for it** —
 * today that is macOS. The page says so in words rather than leaving the platform out
 * silently; that is an acceptance criterion of AKL-803.
 */
export type TransferReleaseDto = {
  /** As published, e.g. "0.1.1". */
  version: string;
  /** ISO-8601 publish timestamp. */
  published_at: string;
  installers: TransferInstallerDto[];
};

/**
 * Fetch the current Akili Transfer release for the download page (AKL-803).
 *
 * Bypasses the global error interceptor: the page renders its own "not available right
 * now" state, and the API already answers a storage hiccup with a 503, so a generic
 * toast on top would say the same thing twice.
 * @returns the current release
 */
export async function fetchTransferRelease(): Promise<TransferReleaseDto> {
  try {
    const resp = await axios.get("/transfer/release", {
      skipGlobalErrorHandler: true,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}
